import type { CorrectDealLead, DealLeadDetail } from '@/types/api'

// Ratios are stored 0 to 1, so a percent input takes 21 and sends 0.21; money stays in millions.
export type CorrectableKind =
  | 'text'
  | 'longText'
  | 'money'
  | 'percent'
  | 'multiple'

export type CorrectableKey = Exclude<keyof CorrectDealLead, 'version'> &
  keyof DealLeadDetail

export interface CorrectableField {
  key: CorrectableKey
  label: string
  kind: CorrectableKind
}

export interface CorrectableSection {
  title: string
  fields: CorrectableField[]
}

// direction, the tag axes and timingDeadline are deliberately not correctable here.
export const CORRECTABLE_SECTIONS: CorrectableSection[] = [
  {
    title: 'Business',
    fields: [
      { key: 'companyName', label: 'Company', kind: 'text' },
      { key: 'projectName', label: 'Project name', kind: 'text' },
      { key: 'sector', label: 'Sector', kind: 'text' },
      { key: 'subSector', label: 'Sub-sector', kind: 'text' },
      { key: 'geography', label: 'Geography', kind: 'text' },
      { key: 'country', label: 'Country', kind: 'text' },
      { key: 'description', label: 'Description', kind: 'longText' },
    ],
  },
  {
    title: 'Broker',
    fields: [
      { key: 'brokerCompany', label: 'Broker company', kind: 'text' },
      { key: 'brokerName', label: 'Broker name', kind: 'text' },
      { key: 'brokerEmail', label: 'Broker email', kind: 'text' },
    ],
  },
  {
    title: 'Reported figures',
    fields: [
      { key: 'revenue', label: 'Revenue', kind: 'money' },
      { key: 'revenueGrowthPct', label: 'Revenue growth', kind: 'percent' },
      { key: 'ebitda', label: 'EBITDA', kind: 'money' },
      { key: 'ebitdaMarginPct', label: 'EBITDA margin', kind: 'percent' },
      { key: 'grossMarginPct', label: 'Gross margin', kind: 'percent' },
      { key: 'netProfit', label: 'Net profit', kind: 'money' },
      { key: 'askingPrice', label: 'Asking price', kind: 'money' },
      {
        key: 'valuationMultiple',
        label: 'Valuation multiple',
        kind: 'multiple',
      },
    ],
  },
  {
    title: 'Concentration',
    fields: [
      { key: 'largestCustomerPct', label: 'Largest customer', kind: 'percent' },
      {
        key: 'stateRevenueConcentrationPct',
        label: 'Largest state',
        kind: 'percent',
      },
      { key: 'rolloverEquityPct', label: 'Rollover equity', kind: 'percent' },
    ],
  },
  {
    title: 'Transaction',
    fields: [
      { key: 'transactionType', label: 'Transaction type', kind: 'text' },
      { key: 'fundingType', label: 'Funding type', kind: 'text' },
      { key: 'reasonForSale', label: 'Reason for sale', kind: 'longText' },
      { key: 'timingNote', label: 'Timing note', kind: 'longText' },
    ],
  },
]

export const CORRECTABLE_FIELDS: CorrectableField[] =
  CORRECTABLE_SECTIONS.flatMap((section) => section.fields)

export function unitFor(kind: CorrectableKind): string | null {
  if (kind === 'money') return '$M'
  if (kind === 'percent') return '%'
  if (kind === 'multiple') return 'x'
  return null
}

export function isNumericKind(kind: CorrectableKind): boolean {
  return kind === 'money' || kind === 'percent' || kind === 'multiple'
}

function roundForDisplay(value: number): number {
  return Math.round(value * 1e4) / 1e4
}

function readValue(
  lead: DealLeadDetail,
  key: CorrectableKey
): string | number | undefined {
  const value = lead[key]
  if (typeof value === 'string' || typeof value === 'number') return value
  return undefined
}

export function toInputValue(
  lead: DealLeadDetail,
  field: CorrectableField
): string {
  const value = readValue(lead, field.key)
  if (value === undefined || value === null) return ''

  if (typeof value === 'number') {
    if (field.kind === 'percent') return String(roundForDisplay(value * 100))
    return String(roundForDisplay(value))
  }

  return value
}

export type ParsedValue =
  | { ok: true; value: string | number | null }
  | { ok: false; message: string }

export function parseInputValue(
  field: CorrectableField,
  raw: string
): ParsedValue {
  const trimmed = raw.trim()
  if (trimmed.length === 0) return { ok: true, value: null }

  if (!isNumericKind(field.kind)) return { ok: true, value: trimmed }

  const parsed = Number(trimmed.replace(/,/g, ''))
  if (!Number.isFinite(parsed)) {
    return { ok: false, message: `${field.label} must be a number` }
  }

  if (field.kind === 'percent') return { ok: true, value: parsed / 100 }
  return { ok: true, value: parsed }
}

function currentValue(
  lead: DealLeadDetail,
  field: CorrectableField
): string | number | null {
  const value = readValue(lead, field.key)
  if (value === undefined) return null
  if (typeof value === 'number') return value
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

// Tolerance because a percent round-trips through a division.
function differs(
  current: string | number | null,
  next: string | number | null
): boolean {
  if (current === null || next === null) return current !== next
  if (typeof current === 'number' && typeof next === 'number') {
    return Math.abs(current - next) > 1e-9
  }
  return current !== next
}

export type CorrectionDraft = Record<string, string>

export type BuiltCorrection =
  | {
      ok: true
      changes: Partial<CorrectDealLead>
      changedKeys: CorrectableKey[]
    }
  | { ok: false; messages: string[] }

// Only changed fields: the server records their names on the activity entry.
export function buildCorrection(
  lead: DealLeadDetail,
  draft: CorrectionDraft
): BuiltCorrection {
  const changes: Partial<CorrectDealLead> = {}
  const changedKeys: CorrectableKey[] = []
  const messages: string[] = []

  for (const field of CORRECTABLE_FIELDS) {
    const raw = draft[field.key]
    if (raw === undefined) continue

    const parsed = parseInputValue(field, raw)
    if (!parsed.ok) {
      messages.push(parsed.message)
      continue
    }

    if (!differs(currentValue(lead, field), parsed.value)) continue

    changedKeys.push(field.key)
    assignChange(changes, field, parsed.value)
  }

  if (messages.length > 0) return { ok: false, messages }
  return { ok: true, changes, changedKeys }
}

function assignChange(
  changes: Partial<CorrectDealLead>,
  field: CorrectableField,
  value: string | number | null
): void {
  if (isNumericKind(field.kind)) {
    if (value === null || typeof value === 'number') {
      Object.assign(changes, { [field.key]: value })
    }
    return
  }

  if (value === null || typeof value === 'string') {
    Object.assign(changes, { [field.key]: value })
  }
}

export function correctableFor(evidenceKey: string): CorrectableField | null {
  return CORRECTABLE_FIELDS.find((field) => field.key === evidenceKey) ?? null
}
